import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash';

import { IOrderInventory } from '@shared/types/order_inventory';
import { buildPickupInventoryInputCodegen } from '@shared/utils';
import { cuftForOrderInventory } from '@shared/utils/cuft_for_order_inventory';
import { filterInventoryForSave } from '@shared/utils/filter_inventory_for_save';

import { IInventoryCaptureProps, InventoryCapture } from '@admin/components/orders/inventory_capture';
import { IPlanCalculatorInputProps, PlanCalculator } from '@admin/components/orders/plan_calculator';
import { client } from '@admin/libraries/apollo';
import {
  PlanFragment,
  usePickupMoverCountLazyQuery,
  useStoragePlansQuery,
  useStorageSubscriptionsQuery,
} from '@admin/schema';
import { estimatedMoverCount } from '@admin/utils/estimated_mover_count';
import { customPlan, dimensionalPlan } from '@admin/utils/subscribe';

type Address = {
  id?: number | string;
  [key: string]: unknown;
};

const SUBSCRIPTIONS_TO_EXCLUDE = ['Item', 'Mattress', 'Garage', 'Closet', 'Studio', 'Apartment', '10x50'];

function normalizeMovers(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(Number(value)) || Number(value) < 1) {
    return 1;
  }
  return Number(value);
}

/**
 * Pickup order form: legacy inventory capture for booking (item list + live CUFT/movers)
 * plus plan utilization summary. Agent/VW estimation on order show is unchanged (ENG-564).
 *
 * PlanCalculator "Calculated Cubic Footage" is incremental pickup CUFT from the inventory
 * wizard only — not order.estimated_cuft (which can include plan size / agent flows).
 * When no inventory is entered, mover display falls back to referenceMovers so edits do not
 * show 1 mover for orders already estimated via Order Estimates.
 */
export const InventorySection: React.FC<
  IInventoryCaptureProps &
    IPlanCalculatorInputProps & {
      subtype?: 'onboarding' | 'subsequent' | 'final' | 'cancelation';
      address?: Address;
      /** Server mover count; used for display when inventory CUFT is zero */
      referenceMovers?: number | null;
      onCuftCalculation(cuft: number): void;
      onMoverCountUpdate(movers: number): void;
      onPlanSuggestion?(plan: PlanFragment): void;
    }
> = ({
  accountID,
  orderID,
  onChange,
  savedInventory,
  subtype,
  address,
  referenceMovers,
  onCuftCalculation,
  onPlanSuggestion,
  onMoverCountUpdate,
}) => {
  const [inventory, setInventory] = useState<IOrderInventory>({});
  const [inventoryCuft, setInventoryCuft] = useState<number>(0);
  const [inventoryMovers, setInventoryMovers] = useState<number>(1);
  const [suggestedPlan, setSuggestedPlan] = useState<PlanFragment | undefined>(undefined);

  const referenceMoversNormalized = useMemo(() => normalizeMovers(referenceMovers), [referenceMovers]);
  const displayMovers = inventoryCuft > 0 ? inventoryMovers : referenceMoversNormalized;

  const { data: storageData } = useStorageSubscriptionsQuery({ client, variables: { accountID: String(accountID) } });
  const inventoryMetrics = storageData?.inventoryMetrics;
  const storageSubscriptions = storageData?.storageSubscriptions || [];
  const utilization = inventoryMetrics?.cuftUsed ?? 0;

  const { data: planData } = useStoragePlansQuery({ client });
  const plans = useMemo(
    () => planData?.storagePlans.filter(({ name }) => !SUBSCRIPTIONS_TO_EXCLUDE.includes(name)) || [],
    [planData],
  );

  const [getPickupMoverCount] = usePickupMoverCountLazyQuery({
    client,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setInventoryMovers(data.pickupMoverCount);
      onMoverCountUpdate(data.pickupMoverCount);
    },
  });

  useEffect(() => {
    if (!inventory || isEmpty(inventory)) {
      setInventoryCuft(0);
      onCuftCalculation(0);
      return;
    }
    const orderCuft = cuftForOrderInventory(filterInventoryForSave(inventory));
    setInventoryCuft(orderCuft);
    onCuftCalculation(orderCuft);
  }, [inventory, onCuftCalculation]);

  // When pickup inventory CUFT is zero, keep the Angular order's mover count in sync with the
  // server/reference value (reonboarding and empty-inventory flows never run estimation below).
  useEffect(() => {
    if (inventoryCuft === 0) {
      onMoverCountUpdate(referenceMoversNormalized);
    }
  }, [inventoryCuft, referenceMoversNormalized, onMoverCountUpdate]);

  useEffect(() => {
    const totalCuft = inventoryCuft + utilization;
    if (!plans || !plans.length || totalCuft === 0) {
      return;
    }
    const plan = dimensionalPlan(plans, totalCuft) || customPlan(plans, totalCuft);
    setSuggestedPlan(plan);

    if (onPlanSuggestion) {
      onPlanSuggestion({ ...plan });
    }
  }, [inventoryCuft, utilization, plans, onPlanSuggestion]);

  useEffect(() => {
    if (!plans || !plans.length || inventoryCuft === 0) {
      return;
    }
    const planForCuft = dimensionalPlan(plans, inventoryCuft) || customPlan(plans, inventoryCuft);
    const capitalizedSubtype = subtype && subtype.charAt(0).toUpperCase() + subtype.slice(1);

    const pricing = (storageSubscriptions[0] || {}).pricing || {};
    let plan = planForCuft;

    if (pricing.plan && pricing.plan.cuft > planForCuft.cuft) {
      plan = pricing.plan;
    }

    if (orderID) {
      const formattedInventory = buildPickupInventoryInputCodegen({ inventory });
      getPickupMoverCount({ variables: { orderID, inventory: formattedInventory } });
    } else {
      estimatedMoverCount({
        inventory,
        plan,
        cuft: inventoryCuft,
        subtype: capitalizedSubtype,
        addressID: address?.id,
      }).then((moverCount: number) => {
        setInventoryMovers(moverCount);
        onMoverCountUpdate(moverCount);
      });
    }
  }, [
    inventoryCuft,
    inventory.requestedMovers,
    plans,
    address,
    orderID,
    inventory,
    subtype,
    getPickupMoverCount,
    onMoverCountUpdate,
    storageSubscriptions,
  ]);

  const handleOnChange = (updatedInventory: IOrderInventory) => {
    setInventory(updatedInventory);
    onChange(updatedInventory);
  };

  return (
    <>
      <InventoryCapture
        orderID={orderID}
        savedInventory={savedInventory}
        hideMoving={subtype === 'subsequent'}
        onChange={handleOnChange}
      />
      <hr />
      <div className="row">
        <div className="col-sm-6 col-sm-push-6">
          <PlanCalculator
            accountID={accountID}
            cuft={inventoryCuft}
            inventoryMetrics={inventoryMetrics || undefined}
            estimatedMovers={displayMovers}
            subscriptions={storageSubscriptions}
            suggestedPlan={suggestedPlan}
          />
        </div>
      </div>
    </>
  );
};
